import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Play, Stop, CheckCircle, Truck, Scales, Package, ListChecks,
  ArrowRight, Warning, Clock, Coffee, ArrowClockwise, Storefront, Trophy,
} from "@phosphor-icons/react";
import { staffApi, getGps, formatStaffErr } from "../../lib/staffApi";

function fmtMs(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}
function fmtBreakMs(ms) {
  if (!ms || ms < 0) return "0m";
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

export default function StaffDuty() {
  const [s, setS] = useState(null);
  const [ws, setWs] = useState(null); // workforce session
  const [duty, setDuty] = useState(null); // /duty/state
  const [busy, setBusy] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [endNotes, setEndNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        staffApi.get("/staff-app/summary"),
        staffApi.get("/workforce/duty/current"),
        staffApi.get("/staff-app/duty/state"),
      ]);
      setS(r1.data);
      setWs(r2.data);
      setDuty(r3.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[staff] duty summary load failed:", err?.message || err);
      }
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const hb = setInterval(async () => {
      try {
        const gps = await getGps().catch(() => ({ latitude: 0, longitude: 0 }));
        await staffApi.post("/staff-app/ping", {
          latitude: gps.latitude, longitude: gps.longitude, activity: "active",
        });
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[staff] heartbeat ping failed:", err?.message || err);
        }
      }
    }, 30000);
    return () => { clearInterval(t); clearInterval(hb); };
  }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      const gps = await getGps().catch(() => ({ latitude: 0, longitude: 0 }));
      await staffApi.post("/staff-app/start-duty", { latitude: gps.latitude, longitude: gps.longitude });
      toast.success("Duty started");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  const resumeStopped = async () => {
    setBusy(true);
    try {
      const gps = await getGps().catch(() => ({ latitude: 0, longitude: 0 }));
      await staffApi.post("/staff-app/duty/resume", {
        latitude: gps.latitude, longitude: gps.longitude,
      });
      toast.success("Duty resumed for today");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  const takeBreak = async () => {
    setBusy(true);
    try {
      await staffApi.post("/staff-app/break");
      toast.success("Break started");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  const endBreak = async () => {
    setBusy(true);
    try {
      await staffApi.post("/staff-app/duty/break-end");
      toast.success("Back to duty");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  const end = async () => {
    setBusy(true);
    try {
      const gps = await getGps().catch(() => ({ latitude: 0, longitude: 0 }));
      const r = await staffApi.post("/staff-app/end-duty", {
        latitude: gps.latitude, longitude: gps.longitude, notes: endNotes,
      });
      toast.success(`Duty ended · ${r.data.bags} bags · ${r.data.total_kg} kg`);
      setEndOpen(false); setEndNotes("");
      load();
    } catch (e) { toast.error(formatStaffErr(e)); }
    setBusy(false);
  };

  if (!s) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  const onDuty = s.duty && s.duty.start_at && !s.duty.end_at;
  const endedToday = s.duty && s.duty.start_at && s.duty.end_at;
  const dutyState = ws?.status || (onDuty ? "ON_DUTY_ACTIVE" : "OFF_DUTY");
  const onBreak = dutyState === "ON_BREAK" || duty?.on_break;
  const prompt = duty?.prompt || (onDuty ? "active" : endedToday ? "resume" : "start");
  const breakInfo = duty?.break || { used_ms: 0, cap_ms: 3_600_000, remaining_ms: 3_600_000 };
  const breakPct = Math.min(100, Math.round((breakInfo.used_ms / breakInfo.cap_ms) * 100));
  const breakCapReached = breakInfo.remaining_ms <= 0;

  return (
    <div className="space-y-5" data-testid="staff-duty">
      {/* Duty status hero */}
      <div className={`rounded-xl p-5 relative overflow-hidden ${onDuty && !onBreak ? "bg-[#2D5A27] text-white" : onBreak ? "bg-[#C89E3E] text-white" : prompt === "resume" ? "bg-[#1F4A19] text-white" : "bg-white border border-[#E5E2D9]"}`} data-testid="duty-hero">
        <div className="text-[10px] uppercase tracking-[0.24em] opacity-80">
          {onBreak ? "On break" : onDuty ? "On duty" : prompt === "resume" ? "Duty stopped" : "Ready to start"}
        </div>
        <div className="font-heading text-3xl mt-1">
          {onBreak ? "Taking a break" : onDuty ? "Let's roll." : prompt === "resume" ? "Resume your day" : "Start your day"}
        </div>
        {onDuty && s.duty?.start_at && (
          <div className="text-xs opacity-80 mt-1 flex items-center gap-1.5">
            <Clock size={12} /> Started {new Date(s.duty.start_at).toLocaleTimeString()}
          </div>
        )}
        {prompt === "resume" && s.duty?.end_at && (
          <div className="text-xs opacity-80 mt-1 flex items-center gap-1.5">
            <Clock size={12} /> Stopped {new Date(s.duty.end_at).toLocaleTimeString()} · same-day resume available
          </div>
        )}

        {ws && (
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="duty-session-kpis">
            <TinyKpi label="Active" v={fmtMs(ws.active_ms)} inverse={onDuty || onBreak}/>
            <TinyKpi label="Break" v={fmtMs(ws.break_ms)} inverse={onDuty || onBreak}/>
            <TinyKpi label="Idle" v={fmtMs(ws.inactive_ms)} inverse={onDuty || onBreak}/>
          </div>
        )}

        {/* Action buttons */}
        {prompt === "start" && (
          <button onClick={start} disabled={busy} data-testid="btn-start-duty"
                  className="mt-5 w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <Play size={14} weight="fill" />{busy ? "Starting…" : "Start duty"}
          </button>
        )}
        {prompt === "resume" && (
          <button onClick={resumeStopped} disabled={busy} data-testid="btn-resume-duty"
                  className="mt-5 w-full h-12 bg-[#C89E3E] hover:bg-[#B38A2F] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <ArrowClockwise size={14} weight="bold" />{busy ? "…" : "Resume duty"}
          </button>
        )}
        {onDuty && !onBreak && (
          <div className="flex gap-2 mt-5">
            <button onClick={takeBreak} disabled={busy || breakCapReached} data-testid="btn-break"
                    className="flex-1 h-12 bg-white/15 hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed text-white border border-white/20 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2"
                    title={breakCapReached ? "Break quota used up" : "Take a break"}>
              <Coffee size={14}/>{breakCapReached ? "Quota used" : busy ? "…" : "Break"}
            </button>
            <button onClick={() => setEndOpen(true)} data-testid="btn-end-duty"
                    className="flex-1 h-12 bg-white/15 hover:bg-white/25 text-white border border-white/20 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <Stop size={14} weight="fill" />End
            </button>
          </div>
        )}
        {onBreak && (
          <button onClick={endBreak} disabled={busy} data-testid="btn-break-end"
                  className="mt-5 w-full h-12 bg-white/25 hover:bg-white/35 text-white border border-white/30 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <ArrowClockwise size={14} weight="bold"/>{busy ? "…" : "End break"}
          </button>
        )}

        {/* Break quota meter — visible whenever duty exists for today */}
        {(onDuty || onBreak) && (
          <div className="mt-4 p-3 bg-black/10 rounded-md" data-testid="break-quota">
            <div className="flex items-center justify-between text-[11px] opacity-90 mb-1.5">
              <span className="uppercase tracking-[0.16em]">
                <Coffee size={10} className="inline -mt-0.5 mr-1"/>
                Break {onBreak ? "· running" : ""}
              </span>
              <span className="font-mono">
                {fmtBreakMs(breakInfo.used_ms)} / {Math.floor(breakInfo.cap_ms / 60_000)}m
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div className={`h-full ${breakCapReached ? "bg-[#F4C7B9]" : onBreak ? "bg-white" : "bg-white/70"}`}
                    style={{ width: `${breakPct}%` }} />
            </div>
            {breakCapReached && (
              <div className="text-[10px] mt-1 opacity-90">Daily break quota used up.</div>
            )}
          </div>
        )}
      </div>

      {/* Task KPIs */}
      <div className="grid grid-cols-3 gap-3" data-testid="staff-kpis">
        <Kpi icon={ListChecks} label="Tasks" value={`${s.tasks.done}/${s.tasks.total}`} sub="today" />
        <Kpi icon={Package} label="Bags" value={s.bags_collected} sub="today" />
        <Kpi icon={Scales} label="Weight" value={`${s.kg_collected}`} sub="kg" />
      </div>

      {/* Pending from earlier days (last 4d) */}
      {duty?.pending_from_earlier > 0 && (
        <Link to="/staff-app/tasks" className="block" data-testid="pending-earlier-banner">
          <div className="bg-[#F4C7B9]/40 border border-[#F4C7B9] rounded-lg p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#F4C7B9] flex items-center justify-center">
              <Warning size={18} color="#7A2E18" weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#7A2E18]">From earlier days</div>
              <div className="text-sm font-medium">
                {duty.pending_from_earlier} pending pickup{duty.pending_from_earlier > 1 ? "s" : ""} (last 4 days)
              </div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-[#7A2E18]" />
          </div>
        </Link>
      )}

      {/* Iter 86 — "Cleared today" scoreboard: positive counter of
          backlog rows this staff closed out today. Only shows when
          they've actually cleared at least one. */}
      {duty?.cleared_today > 0 && (
        <div className="bg-[#F0F7EE] border border-[#D4E0D1] rounded-lg p-4 flex items-center gap-3"
             data-testid="cleared-today-tile">
          <div className="w-10 h-10 rounded-md bg-[#D4E0D1] flex items-center justify-center">
            <Trophy size={18} color="#2D5A27" weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#2D5A27]">Cleared today</div>
            <div className="text-sm font-medium">
              {duty.cleared_today} backlog task{duty.cleared_today > 1 ? "s" : ""} closed out — nice work
            </div>
          </div>
          <div className="font-heading text-2xl text-[#2D5A27] tabular-nums">
            {duty.cleared_today}
          </div>
        </div>
      )}

      {/* POS quick action — visible on duty */}
      {onDuty && !onBreak && (
        <Link to="/staff-app/pos" className="block" data-testid="pos-cta">
          <div className="bg-[#0F1F0C] text-white rounded-lg p-4 flex items-center gap-3 hover:bg-[#1A3317]">
            <div className="w-10 h-10 rounded-md bg-[#2D5A27] flex items-center justify-center">
              <Storefront size={18} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">POS</div>
              <div className="text-sm font-medium">Scan bags · walk-up invoice</div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-white/60" />
          </div>
        </Link>
      )}

      {/* Pending / In-progress tasks alerts */}
      {s.tasks.in_progress > 0 && (
        <Link to="/staff-app/tasks" className="block">
          <div className="bg-white border border-[#F9E4B7] rounded-lg p-4 flex items-center gap-3" data-testid="staff-wip-alert">
            <div className="w-10 h-10 rounded-md bg-[#F9E4B7]/60 flex items-center justify-center">
              <Truck size={18} color="#7A5A11" weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#7A5A11]">In progress</div>
              <div className="text-sm font-medium">{s.tasks.in_progress} pickup{s.tasks.in_progress > 1 ? "s" : ""} to finish</div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-[#5C5A55]" />
          </div>
        </Link>
      )}
      {s.tasks.pending > 0 && onDuty && (
        <Link to="/staff-app/tasks" className="block">
          <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#D4E0D1] flex items-center justify-center">
              <ListChecks size={18} color="#2D5A27" weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55]">Pending</div>
              <div className="text-sm font-medium">{s.tasks.pending} pickups queued</div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-[#5C5A55]" />
          </div>
        </Link>
      )}

      {!onDuty && s.tasks.total > 0 && prompt === "start" && (
        <div className="bg-[#F9E4B7]/40 border border-[#F9E4B7] rounded-lg p-4 text-sm text-[#7A5A11] flex items-start gap-2" data-testid="duty-required-alert">
          <Warning size={16} className="mt-0.5 shrink-0" />
          <div>
            You have <strong>{s.tasks.total}</strong> task{s.tasks.total > 1 ? "s" : ""} assigned today. Start duty first to begin collection.
          </div>
        </div>
      )}

      {/* End duty modal */}
      {endOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setEndOpen(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="font-heading text-lg mb-2">End duty?</div>
            <div className="text-sm text-[#5C5A55] mb-4">
              You collected <strong>{s.bags_collected}</strong> bags · <strong>{s.kg_collected} kg</strong> today.
            </div>
            <textarea
              data-testid="end-duty-notes"
              value={endNotes}
              onChange={(e) => setEndNotes(e.target.value)}
              placeholder="Notes (optional): e.g., vehicle issue, pending drop-off…"
              rows={3}
              className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEndOpen(false)} className="flex-1 h-11 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
              <button onClick={end} disabled={busy} data-testid="btn-end-duty-confirm"
                      className="flex-1 h-11 bg-[#C25D38] hover:bg-[#A64D2E] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={14} weight="bold" />{busy ? "Ending…" : "Confirm end"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-lg p-3">
    <Icon size={16} color="#5C5A55" weight="duotone" />
    <div className="font-heading text-2xl mt-1.5 leading-tight">{value}</div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#8A8780]">{label} · {sub}</div>
  </div>
);

const TinyKpi = ({ label, v, inverse }) => (
  <div className={`${inverse ? "bg-white/15 border-white/20 text-white" : "bg-white border-[#E5E2D9]"} border rounded px-2 py-1.5`}>
    <div className="text-[9px] uppercase tracking-[0.18em] opacity-70">{label}</div>
    <div className="font-mono text-xs font-semibold">{v}</div>
  </div>
);
